<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useCommonStore } from '@/stores/modules/common'
import type { VendorEquipListRes, VendorEquipListType } from '@/types/black-market'
import type { EquipItemType, EquipQuality } from '@/types/equip'
import { getEquipName } from '@/utils/equip'
import { socketEmitAsync } from '@/utils/web-socket'
import { computed, ref, watch } from 'vue'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { formatNumberWithCommas, formatNumberWithWan } from '@/utils'
import { useUserStore } from '@/stores/modules/user'
import type { ItemType } from '@/types/item'
import { QuestionFilled } from '@element-plus/icons-vue'
import { getVendorRefreshPrice, vipDiscount } from '../data'
import { dayjs } from 'element-plus'
import { onMounted } from 'vue'
import { getDarkGoldOverLimitCount } from '@/core/equip/dark-gold'

const commonStore = useCommonStore()
const userStore = useUserStore()

const vendorEquipList = ref<VendorEquipListType[]>([])
const equipInfo = ref<EquipItemType & { price: number }>()
const isBuy = ref(false)
const refreshCount = ref(0)

const itemList = computed(() => {
  return vendorEquipList.value
    .map((item) => {
      const info = equipCollection[item.equipId]
      return {
        ...info,
        ...item,
        reinforcedLevel: 0,
        isLock: false,
        price: item.price,
      }
    })
    .sort((a, b) => {
      const qualityOrder: EquipQuality[] = ['supreme', 'myth', 'darkGold', 'peerless', 'antiquity', 'legend', 'unique', 'epic', 'race', 'normal']
      const qualityDiff = (qualityOrder.indexOf(a.quality) || 0) - (qualityOrder.indexOf(b.quality) || 0)
      if (qualityDiff !== 0) return qualityDiff

      const overLimitDiff = (getDarkGoldOverLimitCount(b.darkGoldAttrs) || 0) - (getDarkGoldOverLimitCount(a.darkGoldAttrs) || 0)
      if (overLimitDiff !== 0) return overLimitDiff

      return (b.supremeAttrs?.level || 0) - (a.supremeAttrs?.level || 0)
    })
})

async function getVendorEquipList() {
  const res = await socketEmitAsync<VendorEquipListRes>({ type: 'getVendorEquipList' })
  vendorEquipList.value = res.vendorEquipList
  isBuy.value = res.isBuy
  refreshCount.value = res.refreshCount
  userStore.vendorResetTime = dayjs().format('YYYY-MM-DD HH:mm:ss')
}
function buyVendorEquip() {
  if (!equipInfo.value?.name) return ElMessage.warning('请先选择商品')
  if (userStore.gold < equipInfo.value.price * vipDiscount[userStore.vipLevel]) return ElMessage.warning('您的金币不足')
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  ElMessageBox.confirm(
    `确定购买【<span class="${equipInfo.value.quality}">${equipInfo.value.name}</span>】吗？（该组商品列表只可购买一件商品）`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!equipInfo.value) return
      const res = await socketEmitAsync<ItemType>({ type: 'buyVendorEquip', data: { id: equipInfo.value.id } })
      userStore.itemList.push(res)
      userStore.gold -= Math.round(equipInfo.value.price * vipDiscount[userStore.vipLevel])
      isBuy.value = true
    })
    .catch(() => {})
}
function refreshVendorEquip() {
  const pirce = getVendorRefreshPrice(refreshCount.value)
  if (userStore.diamond < pirce) return ElMessage.warning('您的钻石不足')
  ElMessageBox.confirm(
    `<p class="affix">确定刷新商品列表吗？本次刷新花费<span class="diamond">${getVendorRefreshPrice(
      refreshCount.value,
    )}</span>钻石，刷新后您将<span class="hp">无法</span>购买本组商品</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<VendorEquipListType[]>({ type: 'refreshVendorEquip' })
      vendorEquipList.value = res
      userStore.diamond -= pirce
      isBuy.value = false
      refreshCount.value++
      equipInfo.value = undefined
    })
    .catch(() => {})
}
function getRealPrice(price: number) {
  const vipLevel = userStore.vipLevel
  if (vipLevel === 0) return formatNumberWithCommas(price)
  return `${formatNumberWithCommas(Math.round(price * vipDiscount[userStore.vipLevel]))}`
}

watch(
  () => commonStore.path,
  (val) => {
    if (val === 'BlackMarket' && !vendorEquipList.value.length) {
      getVendorEquipList()
    }
  },
)
</script>

<template>
  <div class="vendor-wrap">
    <p class="property">
      <span>💰 {{ formatNumberWithWan(userStore.gold) }}</span>
      <span style="margin-left: 12px">💎 {{ formatNumberWithWan(userStore.diamond) }}</span>
    </p>
    <div class="vendor">
      <div class="left">
        <div class="border-wrap" style="flex: 1; display: flex; flex-flow: column nowrap; overflow: hidden">
          <h4>商品列表</h4>
          <div class="vendor-equip-list-wrap">
            <div class="vendor-equip-list">
              <div class="item affix" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  type="primary"
                  plain
                  @click="equipInfo = item"
                  :style="{ borderColor: item.id === equipInfo?.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right border-wrap">
        <h4>装备信息</h4>
        <template v-if="equipInfo">
          <EquipInfo :equip-info="equipInfo" :show-action="false" />
        </template>
      </div>
    </div>
    <div class="action border-wrap">
      <div class="help-info">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>摊贩说明</h4>
            <p>1. 摊贩商品每日刷新。</p>
            <p>
              2. 每一组商品列表只可购买一件，商品价格随每次刷新浮动（<span class="gold">200万 ~ 400万</span>），<span class="unique">独特</span
              >的价格为<span class="grow">8折</span>，<span class="epic">史诗</span>的价格为<span class="grow">7折</span>。
            </p>
            <p>3. 您每日可以免费刷新商店列表，并额外获得一次购买机会，第二次刷新需500钻石，而后价格指数增长</p>
            <p>
              4. 会员等级会带来一定的折扣（售价中展示的已是折扣后的价格）：1级<span class="grow">98折</span>；2级<span class="grow">95折</span
              >；3级<span class="grow">9折</span>
            </p>
            <p>
              5. <span class="info">进阶</span>后，黑市将刷新<span class="darkGold">暗金</span>装备，价格为<span class="gold">500万 ~ 1000万</span
              >，若装备具备<span class="info">超限</span>，则价格会更加高昂。
            </p>
          </div>
        </el-popover>
      </div>
      <h4>交易</h4>
      <p>装备名称：<span v-html="getEquipName(equipInfo, equipInfo?.type)"></span></p>
      <p class="affix">
        售价：
        <template v-if="equipInfo">
          <span class="gold" style="margin-right: 5px">{{ getRealPrice(equipInfo.price) }}</span>
          <span class="un-equipped" style="text-decoration: line-through; font-size: 14px" v-show="userStore.vipLevel">
            {{ formatNumberWithCommas(equipInfo.price) }}
          </span>
        </template>
        <span v-else>（未选择）</span>
      </p>
      <el-button type="success" plain :disabled="isBuy" @click="buyVendorEquip" style="margin-bottom: 10px">
        {{ isBuy ? '已购买' : '购买' }}
      </el-button>
      <el-button type="primary" plain @click="refreshVendorEquip" style="margin-left: 0">
        刷新（{{ refreshCount ? `${formatNumberWithCommas(getVendorRefreshPrice(refreshCount))}钻石` : '免费' }}）
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.vendor-wrap {
  .border-wrap {
    padding: 10px;
  }
  .property {
    font-size: 14px;
    margin: 0 0 10px 0;
    text-align: right;
  }
  .vendor {
    flex: 1;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    overflow: hidden;
    .left {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
    }
    .right {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
    }

    .vendor-equip-list-wrap {
      margin-bottom: 10px;
      flex: 1;
      overflow: auto;
      .vendor-equip-list {
        padding: 0 5px;
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
        overflow: auto;
        .item {
          width: 100%;
          margin-bottom: 10px;
          button {
            width: 100%;
          }
        }
      }
    }
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  .action {
    flex-shrink: 0;
    position: relative;
    button {
      width: 100%;
    }
    p {
      margin: 8px 0;
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  :deep() {
    .el-button {
      width: 100%;
    }
  }
}
</style>
